import React from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  People,
  Description,
  Assignment,
  TrackChanges,
  Webhook,
  AccountCircle,
  Logout,
} from "@mui/icons-material";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import PrivateRoute from "./components/PrivateRoute";
import UserList from "./components/UserList";
import DocumentList from "./components/DocumentList";
import DocumentAssignment from "./components/DocumentAssignment";
import SignatureTracker from "./components/SignatureTracker";
import WebhookManager from "./components/WebhookManager";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#dc004e",
    },
  },
});

const AppContent: React.FC = () => {
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            DocuSign POC
          </Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Button
              color="inherit"
              component={Link}
              to="/users"
              startIcon={<People />}
            >
              Users
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/documents"
              startIcon={<Description />}
            >
              Documents
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/assign"
              startIcon={<Assignment />}
            >
              Assign Documents
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/signatures"
              startIcon={<TrackChanges />}
            >
              Signatures
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/webhooks"
              startIcon={<Webhook />}
            >
              Webhooks
            </Button>

            <Box sx={{ ml: 2 }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <AccountCircle />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem disabled>
                  <Typography variant="subtitle2">
                    {user?.name} ({user?.email})
                  </Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <Logout sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg">
        <Routes>
          <Route path="/" element={<UserList />} />
          <Route path="/users" element={<UserList />} />
          <Route path="/documents" element={<DocumentList />} />
          <Route path="/assign" element={<DocumentAssignment />} />
          <Route path="/signatures" element={<SignatureTracker />} />
          <Route path="/webhooks" element={<WebhookManager />} />
        </Routes>
      </Container>
    </>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <PrivateRoute>
            <AppContent />
          </PrivateRoute>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
